import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Check, Plus } from 'lucide-react';

import { api } from '../api/client.js';
import './AddToPipelineModal.css';

/**
 * Modal dialog: pick a startup + stage to add this investor to a deal pipeline.
 * Props:
 *   - investor: {id, name}
 *   - onClose: () => void
 *   - onCreated: (deal) => void
 */
export function AddToPipelineModal({ investor, onClose, onCreated }) {
    const queryClient = useQueryClient();
    const [startupId, setStartupId] = useState(null);
    const [stage, setStage] = useState('Reached Out');
    const [notes, setNotes] = useState('');

    const { data: startups, isLoading: loadingStartups } = useQuery({
        queryKey: ['startups'],
        queryFn: () => api.get('/startups'),
    });

    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get('/settings'),
    });

    // Which startups does this investor already have a deal on? We hide those.
    const { data: existingDeals } = useQuery({
        queryKey: ['investor-deals', investor.id],
        queryFn: () => api.get(`/deals?investor_id=${investor.id}`),
        // Note: the GET /api/deals endpoint doesn't filter by investor_id natively,
        // so we do a client-side filter below.
    });

    const existingStartupIds = new Set(
        (existingDeals || []).filter(d => d.investor_id === investor.id).map(d => d.startup_id)
    );

    const availableStartups = (startups || []).filter(s => !existingStartupIds.has(s.id));
    const stages = settings?.deal_stages || [
        'Wishlist', 'Reached Out', 'Prospects', 'On Hold',
        'Leads', 'Deal Closed', 'Deal Lost',
    ];

    const createMutation = useMutation({
        mutationFn: (payload) => api.post('/deals', payload),
        onSuccess: (deal) => {
            queryClient.invalidateQueries({ queryKey: ['board'] });
            queryClient.invalidateQueries({ queryKey: ['investor-deals'] });
            onCreated?.(deal);
            onClose();
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!startupId) return;
        createMutation.mutate({
            investor_id: investor.id,
            startup_id: startupId,
            stage,
            notes: notes.trim() || undefined,
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                <header className="modal-header">
                    <div>
                        <h2 className="modal-title">Add to deals</h2>
                        <p className="modal-subtitle">{investor.name}</p>
                    </div>
                    <button className="modal-close" onClick={onClose}>
                        <X size={16} strokeWidth={2}/>
                    </button>
                </header>

                <form className="modal-body" onSubmit={handleSubmit}>
                    <label className="detail-field">
                        <span className="detail-field-label">Portfolio company *</span>
                        {loadingStartups ? (
                            <span className="spinner" />
                        ) : availableStartups.length === 0 ? (
                            <div className="modal-empty">
                                This investor is already on the board for every portfolio company.
                            </div>
                        ) : (
                            <div className="startup-pick-list">
                                {availableStartups.map(s => (
                                    <label key={s.id}
                                           className={`startup-pick${startupId === s.id ? ' is-active' : ''}`}>
                                        <input
                                            type="radio"
                                            name="startup"
                                            value={s.id}
                                            checked={startupId === s.id}
                                            onChange={() => setStartupId(s.id)}
                                        />
                                        <span className="startup-pick-name">{s.name}</span>
                                        {s.sector && <span className="startup-pick-meta">{s.sector}</span>}
                                    </label>
                                ))}
                            </div>
                        )}
                    </label>

                    <label className="detail-field">
                        <span className="detail-field-label">Starting stage</span>
                        <select
                            value={stage}
                            onChange={(e) => setStage(e.target.value)}
                            className="detail-field-input"
                        >
                            {stages.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </label>

                    <label className="detail-field">
                        <span className="detail-field-label">Initial note (optional)</span>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="detail-field-input"
                            rows={2}
                            placeholder="Why are we putting this investor on the board?"
                        />
                    </label>

                    {createMutation.isError && (
                        <div className="panel-error">{createMutation.error.message}</div>
                    )}

                    <div className="modal-actions">
                        <button type="button" className="btn-ghost" onClick={onClose}
                                disabled={createMutation.isPending}>
                            <X size={14} strokeWidth={2}/> Cancel
                        </button>
                        <button type="submit" className="btn-save"
                                disabled={!startupId || createMutation.isPending}>
                            {createMutation.isPending
                                ? <span className="spinner" />
                                : <><Plus size={14} strokeWidth={2}/> Add to deals</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
